import _ from 'lodash'
import moment from 'moment'
import React from 'react'
import { compose } from 'recompose'
import { Field, InjectedFormProps, reduxForm } from 'redux-form'

import { NEUTRAL_COLOURS } from 'constants/colors'
import { DISPLAY_SHORT_DATE_FORMAT } from 'constants/date'
import { Option } from 'constants/models'
import { ChildBooking } from 'services/booking/childBooking/constants'
import { Child } from 'services/child/constants'
import { Nursery } from 'services/nurseries/models'
import { STATUS_OPTIONS_VALUES } from 'services/users/constants'

import { isRequired } from 'utils/fieldValidation'

import { Banner, Button, DateString, Flex, Form, Space, Typography } from 'components'
import ChildSessionsList from 'module/Children/Child/ChildBookingPattern/ChildSessions/components/ChildSessionsList'
import RegularBookingsListItem from
  'module/Finance/RegularBookings/RegularBookingsList/components/RegularBookingsListItem'

import i18n from 'translations'

export interface SendRegistrationFormModalFormValues {
  recipient?: Option
}

interface SendRegistrationFormModalFormProps {
  child: Child
  childBookings: ChildBooking[]
  childSessions?: {
    current: any[]
    future: any[]
    past: any[]
  }
  hasAccessToEditRegularBooking: boolean
  isFinanceV3Enabled: boolean
  isSubmitting: boolean
  nursery?: Nursery
  onCancelClick: () => void
  onCreateRegularBookingClick: () => void
  onEditChildBookingClick: (childBooking: ChildBooking) => void
  onEnrolmentDetailEditClick: () => void
  onSubmit: (fields: SendRegistrationFormModalFormValues) => void
}

export const SEND_REGISTRATION_FORM = 'SendRegistrationForm'

type FinalProps = InjectedFormProps<SendRegistrationFormModalFormValues, SendRegistrationFormModalFormProps>
  & SendRegistrationFormModalFormProps

const SendRegistrationFormModalForm: React.FC<FinalProps> = ({
  child,
  childBookings,
  childSessions,
  handleSubmit,
  hasAccessToEditRegularBooking,
  isFinanceV3Enabled,
  isSubmitting,
  nursery,
  onCancelClick,
  onCreateRegularBookingClick,
  onEditChildBookingClick,
  onEnrolmentDetailEditClick,
  onSubmit,
}) => {
  const { nurserySettings } = nursery
  const { formBuilder } = nurserySettings
  const { agreement, enrollmentDetails, includeBookingPattern, termsAndConditions } = formBuilder
  const isAgreementExists = !!(agreement?.trim())
  const isTermsAndConditionExists = !!(termsAndConditions?.trim())

  const renderBookingPattern = () => {
    if (!childBookings?.length && !childSessions?.current?.length) {
      return (
        <React.Fragment>
          <Typography>{i18n.t('modals:SendRegistrationForm:Form:emptyBookingPatternText1')}</Typography>
          <Typography>{i18n.t('modals:SendRegistrationForm:Form:emptyBookingPatternText2')}</Typography>
        </React.Fragment>
      )
    }

    if (isFinanceV3Enabled) {
      return _.map(childBookings, (childBooking: ChildBooking) => (
        <RegularBookingsListItem
          childBooking={childBooking}
          editButtonComponent={(
            <Button
              hierarchy="tertiary"
              label={i18n.t('global:Edit')}
              margin="-4px -8px 0 0"
              onClick={() => onEditChildBookingClick(childBooking)}
            />
          )}
          hasAccessToEditRegularBooking={hasAccessToEditRegularBooking}
          key={childBooking.id}
          isChildContext
        />
      ))
    }

    const renderChildSession = (childSession) => {
      const { attendancePeriod, endDate, id, isOngoing, startDate } = childSession

      const startDateText = moment(startDate).format(DISPLAY_SHORT_DATE_FORMAT)
      const endDateText = isOngoing
        ? i18n.t('module:Children:Child:BookingPattern:Sessions:List:Current:ongoing')
        : moment(endDate).format(DISPLAY_SHORT_DATE_FORMAT)

      return (
        <React.Fragment key={id}>
          <Typography fontSize={18} fontWeight="600" margin="0 0 10px">
            {`${startDateText} - ${endDateText}`}
          </Typography>
          <ChildSessionsList
            attendancePeriodText={`${attendancePeriod?.name}${attendancePeriod?.isArchived
              ? ` (${i18n.t('global:archived')})`
              : ''}`}
            childSession={childSession}
          />
          <Space space="-50px" />
        </React.Fragment>
      )
    }

    return _.map(childSessions?.current, renderChildSession)
  }

  return (
    <React.Fragment>
      {!isAgreementExists && !isTermsAndConditionExists && (
        <Banner.Warning>
          {i18n.t('modals:SendRegistrationForm:Form:emptyAgreementAndTermsSectionText')}
        </Banner.Warning>
      )}
      {isAgreementExists && !isTermsAndConditionExists && (
        <Banner.Warning>
          {i18n.t('modals:SendRegistrationForm:Form:emptyTermsSectionText')}
        </Banner.Warning>
      )}
      {!isAgreementExists && isTermsAndConditionExists && (
        <Banner.Warning>
          {i18n.t('modals:SendRegistrationForm:Form:emptyAgreementSectionText')}
        </Banner.Warning>
      )}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Row label={i18n.t('modals:SendRegistrationForm:Form:Recipient:label')} required verticalLabel>
          <Field
            childId={child?.id}
            component={Form.InfiniteDropdowns.Carers}
            extraFields={['sublabel', 'email']}
            name="recipient"
            placeholder={i18n.t('modals:SendRegistrationForm:Form:Recipient:placeholder')}
            status={STATUS_OPTIONS_VALUES.ACTIVE}
            validate={isRequired}
            fullWidth
            parentalResponsibilityOnly
            searchable
            withChildRelation
          />
        </Form.Row>
        {(enrollmentDetails || includeBookingPattern) && (
          <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14} margin="0 0 16px 0">
            {i18n.t('modals:SendRegistrationForm:Form:enrolmentInfo')}
          </Typography>
        )}
        {enrollmentDetails && (
          <Flex flexDirection="column" margin="0 0 40px">
            <Flex alignItems="center" justifyContent="space-between" margin="0 0 16px">
              <Typography variant="h5">
                {i18n.t('module:Children:Child:Add:EnrolmentDetails:title')}
              </Typography>
              <Button
                hierarchy="tertiary"
                label={i18n.t('global:Edit')}
                margin="-8px 0"
                onClick={onEnrolmentDetailEditClick}
              />
            </Flex>
            <Flex gap="26px">
              <Flex flexDirection="column" gap="4px">
                <Typography fontSize={14}>
                  {_.upperFirst(i18n.t('global:startDate'))}
                </Typography>
                {child?.startDate ? <DateString date={child?.startDate} /> : '-'}
              </Flex>
              <Flex flexDirection="column" gap="4px">
                <Typography fontSize={14}>
                  {i18n.t('global:LeavingDate')}
                </Typography>
                {child?.leavingDate ? <DateString date={child?.leavingDate} /> : '-'}
              </Flex>
              <Flex flexDirection="column" gap="4px">
                <Typography fontSize={14}>
                  {i18n.t('global:Room')}
                </Typography>
                {child?.nurseryClass?.name ?? '-'}
              </Flex>
              <Flex flexDirection="column" gap="4px">
                <Typography fontSize={14}>
                  {i18n.t('global:KeyPerson')}
                </Typography>
                {child?.keyWorkers?.length ? _.map(child?.keyWorkers, (keyPerson, index) => (
                  <React.Fragment key={keyPerson?.id}>
                    {keyPerson?.fullName}
                    {child.keyWorkers.length - 1 !== index && (
                      <React.Fragment>
                        ,
                        <br />
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )) : null}
              </Flex>
            </Flex>
          </Flex>
        )}
        {includeBookingPattern && (
          <Flex flexDirection="column" margin="0 0 16px">
            <Flex alignItems="center" justifyContent="space-between" margin="0 0 16px">
              <Typography variant="h5">
                {i18n.t('module:Children:Child:Menu:bookingPattern')}
              </Typography>
              {!childBookings?.length && !childSessions?.current?.length ? (
                <Button
                  hierarchy="tertiary"
                  label={i18n.t('global:Create')}
                  margin="-8px 0"
                  onClick={onCreateRegularBookingClick}
                />
              ) : undefined}
            </Flex>
            {renderBookingPattern()}
          </Flex>
        )}
        <Form.FooterActions
          isSubmitting={isSubmitting}
          submitDisabled={isSubmitting}
          submitLabel={i18n.t('global:Send')}
          onCancelClick={onCancelClick}
        />
      </Form>
    </React.Fragment>
  )
}

const validate = (fields) => {
  const errors = {} as any

  if (!fields?.recipient?.email) {
    errors.recipient = i18n.t('modals:SendRegistrationForm:Form:Recipient:emailNotFoundError')
  }

  return errors
}

const enhance = compose(
  reduxForm<
    SendRegistrationFormModalFormValues,
    SendRegistrationFormModalFormProps
  >({ form: SEND_REGISTRATION_FORM, validate }),
)

export default enhance(SendRegistrationFormModalForm)
